import { forwardRef } from "react";

import { clsx } from "clsx";

import type { ButtonHTMLAttributes } from "react";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: `primary` | `secondary` | `outline` | `destructive`;
  size?: `sm` | `md` | `lg`;
  isLoading?: boolean;
  loadingText?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = `primary`, size = `md`, isLoading = false, loadingText, children, disabled, ...props },
    ref,
  ) => {
    const baseClasses = `btn`;

    const variantClasses = {
      primary: `btn-primary`,
      secondary: `btn-secondary`,
      outline: `btn-outline`,
      destructive: `bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600`,
    };

    const sizeClasses = {
      sm: `h-8 px-3 text-xs`,
      md: `h-10 px-4 py-2`,
      lg: `h-12 px-8`,
    };

    return (
      <button
        ref={ref}
        disabled={disabled ?? isLoading}
        type="button"
        className={clsx(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          isLoading && `opacity-50 cursor-not-allowed`,
          className,
        )}
        {...props}
      >
        {isLoading ? (
          <div className="flex items-center">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
            {loadingText ?? children}
          </div>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = `Button`;
